import { isChromatic, mixColour, similarColours } from "./brand.js";

/**
 * The brand's colours as a video wears them (ADR 0060), read once, here, so the renderer, the styles and the Look
 * panel's preview draw the same thing. A palette is whatever the owner kept, in no promised order, so its colours are
 * read by role: the darkest is the dark, the lightest the light, the most colourful of the rest the colour to mark
 * with. Nothing here is a colour of Joogoos's own: the only constants are white and black (2026-09-29, when a fire-safety
 * firm's slate came out near black and its light ground was our off-white).
 *
 * The colour maths the renderer and the scene kit each kept a copy of lives here too.
 */

const rgb = (h: string): [number, number, number] => {
  const x = h.replace("#", "");
  const full = x.length === 3 ? [...x].map((c) => c + c).join("") : x.padEnd(6, "0");
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
};
const hex = (c: readonly number[]) => "#" + c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");
const lin = (v: number) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };

/** WCAG's relative luminance: 0 is black, 1 is white. */
export function luminance(h: string): number {
  const [r, g, b] = rgb(h);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** How far two colours stand apart, as WCAG measures it: 1 is invisible, 21 is black on white. */
export function contrast(a: string, b: string): number {
  const x = luminance(a), y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

// OKLab, for moving a colour's lightness without washing it out: mixing towards white greys a green and mixing towards
// black muddies a yellow, where moving its lightness at the same hue and chroma keeps it the brand's colour.
const fromLin = (x: number) => 255 * (x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055);
function oklab(h: string) {
  const [r, g, b] = rgb(h).map(lin) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return { L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s };
}
function fromOklab(L: number, a: number, b: number): [number, number, number] | null {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3, m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3, s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const out = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  return out.every((x) => x >= -0.0005 && x <= 1.0005) ? (out.map((x) => fromLin(Math.max(0, Math.min(1, x)))) as [number, number, number]) : null;
}

/** How far apart two colours are as a person sees them (OKLab): about 0.02 is a hair, 0.2 another colour. */
function distance(a: string, b: string): number {
  const x = oklab(a), y = oklab(b);
  return Math.hypot(x.L - y.L, x.a - y.a, x.b - y.b);
}

/**
 * How far a colour may be moved to hold a contrast and still be the brand's colour: the firm's red lifted to 3:1 on its
 * slate moves 0.05 and is still its red; lifted to 3:1 on a blue it moved 0.2 and was salmon, and a yellow deepened
 * to 3:1 on white moved 0.2 and was mustard (2026-09-29). Past it, the other ground's colour is used instead.
 */
const STEP = 0.06;

/**
 * A dark of `c`'s own hue for a brand whose darkest colour is not dark (a yellow, a pink, an orange): deepened until
 * white words held on it, those went olive-brown, mauve and rust, as whole grounds behind long type (2026-09-29).
 */
function nightOf(c: string): string {
  const o = oklab(c), C = Math.min(0.03, Math.hypot(o.a, o.b)), h = Math.atan2(o.b, o.a);
  return hex(fromOklab(0.25, C * Math.cos(h), C * Math.sin(h)) ?? [0, 0, 0]);
}

/** A light colour that is a page, not a colour: a cream, a pale tint, an off-white (OKLab lightness and chroma). */
function isPage(c: string): boolean {
  const o = oklab(c);
  return o.L >= 0.9 && Math.hypot(o.a, o.b) <= 0.07;
}

/**
 * `c` made lighter at the same hue until it holds `ratio` against `ground`: chroma is kept as far as the screen can
 * show it at that lightness. White when nothing does.
 */
export function lift(c: string, ground: string, ratio: number): string {
  const o = oklab(c), C = Math.hypot(o.a, o.b), hA = Math.atan2(o.b, o.a);
  for (let L = o.L; L <= 1.0001; L += 0.01) {
    for (let k = 1; k >= 0; k -= 0.05) {
      const x = fromOklab(L, C * k * Math.cos(hA), C * k * Math.sin(hA));
      if (x) { const h = hex(x); if (contrast(h, ground) >= ratio) return h; break; }
    }
  }
  return "#ffffff";
}

/**
 * `c` made darker at the same hue until it holds `ratio` against `ground`, the mirror of `lift`; `c` itself when it
 * holds already, black when nothing does. Never a mix towards black: a yellow mixed towards black goes olive-grey, and
 * a slate mixed 45% of the way by rule went near black (2026-09-29). The steps are fine, so a colour a hair short of
 * holding is moved a hair.
 */
export function deepen(c: string, ground: string, ratio: number): string {
  if (contrast(c, ground) >= ratio) return c;
  const o = oklab(c), C = Math.hypot(o.a, o.b), hA = Math.atan2(o.b, o.a);
  for (let i = 1; o.L - i * 0.002 >= -0.001; i++) {
    const L = Math.max(0, o.L - i * 0.002);
    for (let k = 20; k >= 0; k--) {
      const x = fromOklab(L, (C * k / 20) * Math.cos(hA), (C * k / 20) * Math.sin(hA));
      if (x) { const h = hex(x); if (contrast(h, ground) >= ratio) return h; break; }
    }
  }
  return "#000000";
}

/** `c` moved towards white (when it is lighter than the ground) or black until it holds `ratio`; null if it never does. */
function away(c: string, ground: string, ratio: number): string | null {
  const end = luminance(c) >= luminance(ground) ? "#ffffff" : "#000000";
  for (let i = 0; i <= 50; i++) { const m = mixColour(c, end, i / 50); if (contrast(m, ground) >= ratio) return m; }
  return null;
}

/**
 * The words to set on `ground`: whichever of `light` and `dark` reads better, moved towards white or black until it
 * holds `ratio` (WCAG's 4.5:1 for words). The other is tried when the better one cannot get there, and white or black
 * always can: no colour is under 4.5:1 against both.
 */
export function wordsOn(ground: string, light: string, dark: string, ratio = 4.5): string {
  const [first, second] = contrast(light, ground) >= contrast(dark, ground) ? [light, dark] : [dark, light];
  return away(first, ground, ratio) ?? away(second, ground, ratio) ?? (contrast("#ffffff", ground) >= contrast("#000000", ground) ? "#ffffff" : "#000000");
}

/**
 * The brand's colour as a mark on `ground` (a rule, a dot, a full stop, a word that matters): itself when it holds
 * `ratio` (3:1, a shape's or large type's line), lifted or deepened a small step at its hue when that is enough, and
 * `fallback` — the other ground's colour — otherwise: when it is the ground's own colour, no colour at all, or would
 * have to become another colour to read (a red lifted to salmon, a yellow deepened to mustard, 2026-09-29). The brand's
 * colour is then left to the grounds it stands as whole.
 */
export function markOn(c: string, ground: string, fallback: string, ratio = 3): string {
  if (contrast(c, ground) >= ratio) return c;
  if (similarColours(c, ground) || !isChromatic(c)) return fallback;
  const m = luminance(c) > luminance(ground) ? lift(c, ground, ratio) : deepen(c, ground, ratio);
  return distance(m, c) <= STEP ? m : fallback;
}

/**
 * The page a logo sits on: the brand's light when it is a near-white, else white. Logos are drawn for white pages, and
 * a yellow wordmark on a tile of the brand's yellow vanished (2026-09-29).
 */
export function tileOf(paper: string): string {
  return isPage(paper) && !isChromatic(paper) ? paper : "#ffffff";
}

/**
 * The brand's colours by role.
 *
 * - `paper`, the light ground and the words on the dark: the palette's lightest when it is a page (a cream, a pale
 *   tint, an off-white), otherwise white breathed 4% towards the brand's dark. White is the page every logo is drawn
 *   for; a yellow or a pink taken for the light put the colour behind every word of a Clean video and left no white in
 *   a Showreel (2026-09-29), so a light colour is a colour, with grounds of its own. The breath keeps a white card
 *   visible on it, and gives it the brand's temperature.
 * - `ink`, the dark ground and the words on the light: the palette's darkest as it is, deepened at its hue only if the
 *   light's words would not hold 4.5:1 on it. A darkest that is not dark at all (a yellow, a pink) would have to become
 *   another colour to carry white words, so the dark is a near-black of its hue instead.
 * - `accent`, the colour to mark with and to give whole grounds: the most colourful of the rest (ties go to the
 *   palette's order), else the more colourful of the darkest and the lightest, else the dark.
 * - `accentGround`: the accent is a colour of its own, not a shade of the dark or of a page the palette gave, so it can
 *   stand as a whole ground. A colour that is itself the dark never can: its video is the colour and white.
 * - `second`, a second dark: another colour of the brand's own that carries the light's words as the dark does (a
 *   logo's blue swoosh beside its slate and its red), so every other dark ground stands on it (`darkGround`). Null when
 *   there is none. A third colour that is none of these is not drawn: the styles take one accent (ADR 0058).
 */
export type BrandRoles = { ink: string; paper: string; accent: string; accentGround: boolean; second: string | null };

const spread = (h: string) => { const c = rgb(h); return Math.max(...c) - Math.min(...c); };
const mostColourful = (cs: readonly string[]) => [...cs].filter(isChromatic).sort((a, b) => spread(b) - spread(a))[0];

export function brandRoles(palette: readonly string[]): BrandRoles {
  const p = [...new Set(palette.map((c) => c.toLowerCase()))];
  if (!p.length) return { ink: "#000000", paper: "#ffffff", accent: "#000000", accentGround: false, second: null };
  const byLight = [...p].sort((a, b) => luminance(a) - luminance(b));
  const darkest = byLight[0]!, lightest = byLight.at(-1)!;
  // Judged against white, so the dark does not depend on the light it will carry.
  const keptDark = distance(deepen(darkest, "#ffffff", 4.5), darkest) <= STEP;
  const dark = keptDark ? darkest : nightOf(darkest);
  const page = isPage(lightest) && lightest !== dark ? lightest : null;
  const paper = page ?? mixColour("#ffffff", dark, 0.04);
  const ink = deepen(dark, paper, 4.5);
  const rest = p.filter((c) => c !== page && !(keptDark && c === darkest));
  const found = mostColourful(rest);
  const accent = found ?? mostColourful([darkest, lightest]) ?? ink;
  const accentGround = !!found && !similarColours(found, ink) && !(page && similarColours(found, page));
  const second = (found && mostColourful(rest.filter((c) => c !== found && contrast(c, paper) >= 4.5 && !similarColours(c, ink) && !similarColours(c, found)))) ?? null;
  return { ink, paper, accent, accentGround, second };
}

/** The colour of the video's `k`-th dark ground, from 0: the dark, and every other one the second dark if there is one. */
export function darkGround(r: BrandRoles, k: number): string {
  return k % 2 === 1 && r.second ? r.second : r.ink;
}

/**
 * A video's last frame, in the Look panel's preview as in the render: the brand's colour when it can be a ground, else
 * its dark; the words that read on it; the logo on its tile (`tileOf`), so a logo in the ground's own colour still shows.
 */
export function endCard(r: BrandRoles): { ground: string; words: string; tile: string } {
  const ground = r.accentGround ? r.accent : r.ink;
  return { ground, words: wordsOn(ground, r.paper, r.ink), tile: tileOf(r.paper) };
}
